/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2026 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
/**
 * Shared helpers for "unified Sentry tagging" used across dropins.
 *
 * This module intentionally does NOT import @sentry/* at runtime.
 * Instead, it operates on "Sentry-like" and "Scope-like" objects.
 */

import * as envUtil from './EnvUtil';
import { logging } from '../services/LoggingAPI';

type AnyRecord = Record<string, any>;

export interface IScopeLike {
  setTag?: (key: string, value: string) => void;
  setContext?: (name: string, context: AnyRecord) => void;
}

export interface ISentryLike {
  withScope?: (cb: (scope: IScopeLike) => void) => void;
  getCurrentScope?: () => IScopeLike | undefined;
}

export interface IBaseTagParams {
  service?: string;
  product?: string;
  env?: string;
  tenantId?: string | number;
  feature?: string;
}

export interface IUnifiedScopeParams extends IBaseTagParams {
  /**
   * Additional tags to apply to the new scope.
   */
  tags?: AnyRecord;
  /**
   * Allows overriding how to locate the Sentry instance.
   */
  getSentry?: () => ISentryLike | undefined;
}

/**
 * Config for dropin-specific Sentry tagging (single init per dropin).
 * Use createDropinSentryTagging(config) so dropins don't copy wrapper code.
 */
export interface IDropinSentryConfig {
  /** Dropin module name, e.g. "dc-files2-dropin". */
  moduleName: string;
  /** Full version string, e.g. "dc-files2-dropin@4.3.0". Used for module.version tag and dropin context. */
  moduleVersion?: string;
  /** Product tag default. */
  product?: string;
  /** Default feature for this dropin. */
  feature?: string;
  /** Timestamp when dropin was loaded (for dropin context). */
  loadedAt?: number;
}

export interface IDropinSentryTagging {
  buildBaseSentryTags: (params?: IBaseTagParams) => Record<string, string>;
  applyTagsToScope: (scope?: IScopeLike, tags?: AnyRecord) => void;
  ensureBaseSentryTags: (params?: IBaseTagParams & { feature?: string }) => void;
  withUnifiedSentryScope: (
    params?: { feature?: string; tags?: AnyRecord },
    fn?: (scope?: IScopeLike, sentry?: ISentryLike) => void
  ) => void;
  sendSentryPerf: (params: { metricName: string; value: number }) => void;
}

const logger = logging.getLogger('SentryTaggingUtil');

export function getSentryFromSdk(): ISentryLike | undefined {
  return (window as any).adobe_dc_sdk?.Sentry;
}

export function getTenantIdFromUrl(search: string = window.location.search): string | undefined {
  try {
    const params = new URLSearchParams(search || '');
    return params.get('tenantId') ?? params.get('tenant_id') ?? undefined;
  } catch (e) {
    logger.warn('Failed to parse tenantId from URL', e);
    return undefined;
  }
}

export function buildBaseSentryTags({
  service,
  product,
  env,
  tenantId,
  feature,
}: IBaseTagParams = {}): Record<string, string> {
  const tags: Record<string, string> = {};

  Object.entries({
    service,
    product,
    env,
    tenantId,
    feature,
  }).forEach(([key, value]) => {
    if (value != null && String(value)) {
      tags[key] = String(value);
    }
  });

  return tags;
}

export function applyTagsToScope(scope?: IScopeLike, tags: AnyRecord = {}) {
  if (!scope || !tags) {
    return;
  }

  Object.keys(tags).forEach(key => {
    const value = tags[key];
    if (value != null && typeof scope.setTag === 'function') {
      scope.setTag(key, String(value));
    }
  });
}

export function ensureBaseSentryTags({
  service,
  product,
  env,
  tenantId,
  feature,
  getSentry = getSentryFromSdk,
}: IUnifiedScopeParams = {}) {
  const Sentry = getSentry?.();
  if (!Sentry) {
    return;
  }

  const resolvedEnv = env ?? envUtil.getHostEnv?.();
  const resolvedTenantId = tenantId ?? getTenantIdFromUrl();
  const baseTags = buildBaseSentryTags({
    service,
    product,
    env: resolvedEnv,
    tenantId: resolvedTenantId,
    feature,
  });

  try {
    const scope = Sentry.getCurrentScope?.();
    applyTagsToScope(scope, baseTags);
  } catch (e) {
    logger.warn('Failed to ensure base Sentry tags', e);
  }
}

export function withUnifiedSentryScope(
  {
    service,
    product,
    env,
    tenantId,
    feature,
    tags,
    getSentry = getSentryFromSdk,
  }: IUnifiedScopeParams = {},
  fn: (scope?: IScopeLike, sentry?: ISentryLike) => void,
) {
  const Sentry = getSentry?.();
  if (typeof Sentry?.withScope !== 'function') {
    fn();
    return;
  }

  const resolvedEnv = env ?? envUtil.getHostEnv?.();
  const resolvedTenantId = tenantId ?? getTenantIdFromUrl();

  const baseTags = buildBaseSentryTags({
    service,
    product,
    env: resolvedEnv,
    tenantId: resolvedTenantId,
    feature,
  });

  Sentry.withScope(scope => {
    applyTagsToScope(scope, {
      ...baseTags,
      ...(tags || {}),
    });
    fn(scope, Sentry);
  });
}

/**
 * Create a dropin-specific Sentry tagging API so dropins can "init with their details"
 * instead of copying a wrapper file. Use once per dropin and re-export or use the returned helpers.
 */
export function createDropinSentryTagging(config: IDropinSentryConfig): IDropinSentryTagging {
  const {
    moduleName,
    moduleVersion,
    product = 'acrobat',
    feature: defaultFeature,
    loadedAt = Date.now(),
  } = config;

  const baseParams = { service: moduleName, product };

  const dropinContextVersion = moduleVersion && moduleVersion.includes('@') ? moduleVersion.split('@').slice(1).join('@') : moduleVersion;

  const applyTags = applyTagsToScope;
  const buildBase = buildBaseSentryTags;
  const ensureBase = ensureBaseSentryTags;

  return {
    buildBaseSentryTags(params: IBaseTagParams = {}) {
      return buildBase({ ...baseParams, ...params });
    },

    applyTagsToScope(scope?: IScopeLike, tags: AnyRecord = {}) {
      applyTags(scope, tags);
    },

    ensureBaseSentryTags(params: IBaseTagParams & { feature?: string } = {}) {
      ensureBase({ ...baseParams, feature: params.feature ?? defaultFeature, ...params });
    },

    withUnifiedSentryScope(
      { feature, tags }: { feature?: string; tags?: AnyRecord } = {},
      fn?: (scope?: IScopeLike, sentry?: ISentryLike) => void,
    ) {
      const effectiveFeature = feature ?? defaultFeature;
      withUnifiedSentryScope(
        {
          ...baseParams,
          feature: effectiveFeature,
          tags: {
            module: moduleName,
            ...(moduleVersion ? { 'module.version': moduleVersion } : {}),
            ...(tags || {}),
          },
        },
        (scope, Sentry) => {
          if (scope && typeof scope.setContext === 'function') {
            scope.setContext('dropin', {
              type: 'dropin',
              name: moduleName,
              ...(dropinContextVersion ? { version: dropinContextVersion } : {}),
              loaded_at: loadedAt,
            });
          }
          if (typeof fn === 'function') {
            fn(scope, Sentry);
          }
        },
      );
    },

    sendSentryPerf({ metricName, value }: { metricName: string; value: number }) {
      withUnifiedSentryScope(
        {
          ...baseParams,
          feature: defaultFeature,
          tags: {
            module: moduleName,
            ...(moduleVersion ? { 'module.version': moduleVersion } : {}),
          },
        },
        scope => {
          // eslint-disable-next-line no-unused-expressions
          scope?.setTag?.(`performance_${metricName}`, value.toString());
          // eslint-disable-next-line no-unused-expressions
          scope?.setContext?.('performance_metric', {
            name: metricName,
            value,
            unit: 'millisecond',
            timestamp: Date.now(),
          });
        },
      );
    },
  };
}
